import { Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import useEmployeeData from 'containers/pages/timesheets/hooks/useEmployeeData';
import TimesheetSelectors from 'containers/pages/timesheets/components/selectors/TimesheetSelectors';
import TimesheetsSummaryCard from 'containers/pages/timesheets/components/summaryCard/TimesheetsSummaryCard';
import TimesheetsToolbar from 'containers/pages/timesheets/components/toolbar/TimesheetsToolbar';
import LoadingBackdrop from 'components/LoadingBackdrop';
import useComments from 'containers/pages/comments/hooks/useComments';
import { useNotificationProvider } from 'hooks';
import CommentsTable from 'containers/pages/comments/components/table/CommentsTable';
import CommentManager from 'containers/pages/comments/components/manager/CommentManager';
import { CANtrackComment } from 'containers/pages/comments/types/comment';

const Timesheets: React.FC = () => {
	const {
		employees,
		selectedEmployee,
		loadingEmployees,
		setSelectedEmployee,
	} = useEmployeeData();

	// Set the selected date to current date - 2 days
	const [selectedDate, setSelectedDate] = useState<Date>(new Date());
	const {
		comments: timesheetEntries,
		loadingComments: loadingTimesheetEntries,
		fetchComments,
		setComments,
	} = useComments();
	const { notifyError } = useNotificationProvider();

	useEffect(() => {
		if (selectedEmployee) {
			fetchEntriesForTimesheets();
		}
	}, [selectedEmployee, selectedDate]);

	const fetchEntriesForTimesheets = async () => {
		if (selectedEmployee) {
			fetchComments({
				employeeId: selectedEmployee.Id,
				date: selectedDate,
			});
		} else {
			setComments([]);
			notifyError('Please select an employee');
		}
	};

	const [decimalFormat, setDecimalFormat] = useState<boolean>(true);
	const [commentToCopy, setCommentToCopy] = useState<CANtrackComment>();
	const [searchQuery, setSearchQuery] = useState('');

	const filteredEntries = useMemo(() => {
		if (!searchQuery.trim()) return timesheetEntries;
		const lower = searchQuery.toLowerCase();
		return timesheetEntries.filter(
			(c) =>
				c.caseTitle?.toLowerCase().includes(lower) ||
				c.title?.toLowerCase().includes(lower) ||
				c.description?.toLowerCase().includes(lower),
		);
	}, [timesheetEntries, searchQuery]);

	const handleCopyComment = (comment: CANtrackComment) => {
		setCommentToCopy({
			...comment,
			id: 0,
		});
	};

	return (
		<div style={{ padding: '1rem' }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					gap: 1,
					alignItems: { xs: 'stretch', md: 'center' },
				}}
			>
				<TimesheetSelectors
					employees={employees}
					fetchTimesheetEntries={fetchEntriesForTimesheets}
					loadingEmployees={loadingEmployees}
					loadingTimesheetEntries={loadingTimesheetEntries}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					selectedEmployee={selectedEmployee}
					setSelectedEmployee={setSelectedEmployee}
				/>
			</Box>
			<Box display="flex" flexDirection={'column'} gap={2}>
				<Box>
					<TimesheetsToolbar
						employeeName={selectedEmployee?.FullName || ''}
						selectedDate={selectedDate}
						decimalFormat={decimalFormat}
						setDecimalFormat={setDecimalFormat}
						loadingEmployees={loadingEmployees}
						loadingTimesheetEntries={loadingTimesheetEntries}
					/>
					<Box display={'flex'} flexDirection={'column'} gap={1}>
						<CommentManager
							setComments={setComments}
							commentToCopy={commentToCopy}
							resetCommentToCopy={() =>
								setCommentToCopy(undefined)
							}
							userId={selectedEmployee?.UserId}
							contactId={selectedEmployee?.ContactId}
							searchQuery={searchQuery}
							onSearchChange={setSearchQuery}
						/>
						<CommentsTable
							comments={filteredEntries}
							loadingComments={loadingTimesheetEntries}
							showDecimalFormat={decimalFormat}
							setComments={setComments}
							onCopyCommentClick={handleCopyComment}
						/>
					</Box>
				</Box>

				<Box display={'flex'} justifyContent={'flex-end'}>
					<TimesheetsSummaryCard
						timesheetEntries={timesheetEntries}
						showDecimal={decimalFormat}
						loadingTimesheetEntries={loadingTimesheetEntries}
					/>
				</Box>
			</Box>
			<LoadingBackdrop open={loadingEmployees} />
		</div>
	);
};

export default Timesheets;
